/**
 * What one opportunity actually involves.
 *
 * The list screen has room for three lines, which is enough to recognise a
 * session and not enough to decide about one. Parinaam asked for the cards to
 * open (9 Oct 2026) because a volunteer giving up a Saturday is choosing
 * between programmes as much as between dates, and the thing that tells them
 * apart — what the programme is for, who it serves, where it happens — was only
 * ever visible to staff.
 *
 * It reads `/sessions/:id/overview`, not the staff `/sessions/:id`. The
 * difference is the roster: this endpoint carries a count and no names, which
 * is the same rule the list screen follows and the reason this page can exist
 * at all.
 *
 * Signing up stays on the list. The training gate belongs with the action, and
 * splitting it across two screens would mean a volunteer could reach the button
 * by a path that skipped the documents.
 */

import { Link, useParams } from 'react-router-dom';
import { formatDuration } from '@parinaam-vms/shared';
import { Card, DetailList, ErrorState, LoadingState, PageHeader } from '../../components/common';
import { useSessionOverview } from '../../hooks/useSessions';
import type { SessionOverview } from '../../services/api/sessions';
import { formatDateRange, formatTimeRange, orDash } from '../../utils/format';
import styles from './OpportunityDetailPage.module.css';

/**
 * How many places are left, in words rather than a fraction.
 *
 * "3 of 10 taken" asks the reader to subtract. The question they have is
 * whether there is room for them.
 */
function placesText(session: SessionOverview): string {
  if (session.volunteers_needed === null) {
    return session.assigned_count === 1
      ? '1 volunteer signed up so far — no limit on numbers'
      : `${session.assigned_count} volunteers signed up so far — no limit on numbers`;
  }

  const left = session.volunteers_needed - session.assigned_count;
  if (left <= 0) return `Full — ${session.volunteers_needed} places, all taken`;
  return left === 1
    ? `1 place left of ${session.volunteers_needed}`
    : `${left} places left of ${session.volunteers_needed}`;
}

/** Who the work is for, as a sentence rather than a list of ids. */
function communitiesText(session: SessionOverview): string {
  if (session.communities.length === 0) return '—';
  return session.communities
    .map((community) => (community.city ? `${community.name} (${community.city})` : community.name))
    .join(', ');
}

export function OpportunityDetailPage() {
  const { id } = useParams<{ id: string }>();
  const overview = useSessionOverview(id);

  if (overview.isPending) return <LoadingState label="Loading the details…" />;

  if (overview.isError) {
    return (
      <>
        <PageHeader title="Activity details" />
        <Card>
          <ErrorState error={overview.error} onRetry={() => void overview.refetch()} />
        </Card>
      </>
    );
  }

  const session = overview.data;

  return (
    <>
      <PageHeader
        title={session.activity_name}
        description={`Part of ${session.program_name}`}
        actions={
          <Link to="/opportunities" className={styles.back}>
            ← Back to Find activities
          </Link>
        }
      />

      {/*
        The session first, because it is the thing being decided about — the
        date somebody is being asked to keep free. The activity and programme
        below it answer "and what is this, exactly?", in that order.
      */}
      <Card title="This session">
        <DetailList
          items={[
            { label: 'Session', value: session.session_name },
            { label: 'When', value: formatDateRange(session.start_date, session.end_date) },
            { label: 'Time', value: formatTimeRange(session.start_time, session.end_time) },
            {
              label: 'Usual length',
              value: session.default_duration_minutes
                ? formatDuration(session.default_duration_minutes)
                : '—',
            },
            { label: 'Places', value: placesText(session) },
            { label: 'Parinaam contact', value: orDash(session.parinaam_rep_name) },
            { label: 'Who it serves', value: communitiesText(session), wide: true },
            {
              label: 'About this session',
              value: orDash(session.session_description),
              wide: true,
            },
          ]}
        />
      </Card>

      <Card title="The activity">
        <DetailList
          items={[
            { label: 'Activity', value: session.activity_name },
            { label: 'Where', value: orDash(session.activity_location) },
            {
              label: 'Runs',
              value: formatDateRange(session.activity_start_date, session.activity_end_date),
            },
            {
              label: 'What it involves',
              value: orDash(session.activity_description),
              wide: true,
            },
          ]}
        />
      </Card>

      <Card title="The programme">
        <DetailList
          items={[
            { label: 'Programme', value: session.program_name },
            {
              label: 'Runs',
              value: session.program_start_date
                ? formatDateRange(session.program_start_date, session.program_end_date)
                : '—',
            },
            {
              label: 'What it is for',
              value: orDash(session.program_description),
              wide: true,
            },
          ]}
        />
      </Card>

      {/*
        Back to the list to sign up, rather than a second button here. The
        training gate lives with the action on that screen, and a button on this
        one would be a way to reach the sign-up without passing it.
      */}
      <p className={styles.signUpNote}>
        Ready to go? <Link to="/opportunities">Sign up from Find activities</Link> — you will be
        asked to read the mandatory training first.
      </p>
    </>
  );
}
